import { isNil } from 'lodash';
import { useContext } from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBClearButton } from '@shared/components/button';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextBody2, MBTextOverlineBold } from '@shared/components/typography';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import {
  getComponentIcon,
  getComponentLabel,
  getComponentPlaceholder,
  isDisplayableComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { getTestId } from '@shared/util/getTestId';
import { EditFieldContainer2 } from './EditFieldContainer2';
import { EditFieldContainerProps } from './EditFieldContainerProps';

export function EditFieldContainer(props: EditFieldContainerProps) {
  const responsiveForms = useResponsiveFormContext();
  if (responsiveForms) {
    return <EditFieldContainer2 {...props} />;
  } else {
    return <EditFieldContainer1 {...props} />;
  }
}

function EditFieldContainer1({
  component,
  children,
  icon = getComponentIcon(component),
  label = getComponentLabel(component),
  placeholder = getComponentPlaceholder(component),
  showIcon = true,
  showLabel = false,
  indentLabel = false,
  showPlaceholder = false,
  showClearButton = false,
  error,
  onPress,
  onClear,
  reserveIconSpace = false,
  includeWrapperPadding = true,
  compactTop = false,
  compactBottom = false,
  testID = getTestId('card-field-cards', component.id),
  anchorName,
}: EditFieldContainerProps) {
  const colors = useContext(ColorThemeContext);
  const isDisplayable = isDisplayableComponent(component);
  const neverHasIcon = component.id === 'name' && isNil(icon);
  const shouldHaveIcon = (showIcon || reserveIconSpace) && isDisplayable && !neverHasIcon;
  const renderIcon = showIcon && shouldHaveIcon && !isNil(icon);
  const renderLabel = showLabel && isDisplayable && !isNil(label);
  const renderPlaceholder = showPlaceholder && isDisplayable && !isNil(placeholder);
  const renderClearButton = showClearButton && !isNil(onClear);
  const hasError = !isNil(error);

  return (
    <MBTouchableOpacity onPress={onPress} testID={testID}>
      <MBView
        style={[
          styles.wrapper,
          includeWrapperPadding && styles.wrapperPadding,
          hasError && { backgroundColor: colors.error.background },
        ]}
      >
        <View style={{ anchorName }}>
          <MBView
            style={[
              styles.row,
              includeWrapperPadding && styles.rowWithWrapperPadding,
              compactTop && styles.compactRow,
              compactBottom && styles.compactRowBottom,
            ]}
          >
            {renderIcon && (
              <RNView style={[styles.icon]}>
                <MBIconV2
                  name={icon}
                  size={40}
                  color={hasError ? colors.error.foreground : colors.accent}
                />
              </RNView>
            )}
            {shouldHaveIcon && !renderIcon && !reserveIconSpace && (
              <RNView style={styles.icon}>
                <MBIconV2
                  name="squares-stacked"
                  size={40}
                  color={hasError ? colors.error.foreground : colors.accent}
                />
              </RNView>
            )}
            {reserveIconSpace && !renderIcon && <RNView style={[styles.icon, styles.iconSpace]} />}
            <RNView
              style={[
                styles.content,
                compactTop && styles.compactContentTop,
                compactBottom && styles.compactContentBottom,
              ]}
            >
              {renderLabel && (
                <MBTextOverlineBold
                  color={hasError ? colors.foregroundInactive : colors.input.label}
                  style={[styles.label, indentLabel && styles.indentedLabel]}
                >
                  {label}
                </MBTextOverlineBold>
              )}
              {renderPlaceholder && (
                <MBView style={styles.placeholder}>
                  <MBTextBody1 color={colors.foregroundInactive}>{placeholder}</MBTextBody1>
                </MBView>
              )}
              {children}
            </RNView>
            {renderClearButton && (
              <MBView style={styles.clearButton}>
                <MBClearButton
                  onPress={onClear}
                  color={hasError ? colors.foregroundInactive : undefined}
                />
              </MBView>
            )}
          </MBView>
        </View>
      </MBView>
      {hasError && (
        <MBView style={[styles.error]}>
          <MBTextBody2 color={colors.error.foreground}>{error}</MBTextBody2>
        </MBView>
      )}
    </MBTouchableOpacity>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    borderRadius: grid(1),
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
  },
  wrapperPadding: {
    marginVertical: grid(0.125),
    marginHorizontal: grid(0.75),
  },
  row: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 60,
    backgroundColor: 'transparent',
    justifyContent: 'space-between',
    paddingHorizontal: grid(3),
  },
  rowWithWrapperPadding: {
    paddingHorizontal: grid(2),
  },
  icon: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'row',
    height: '100%',
    marginRight: grid(2),
    paddingTop: grid(1.5),
  },
  iconSpace: {
    width: grid(5),
  },
  label: {
    marginBottom: grid(0.5),
  },
  placeholder: {
    paddingVertical: grid(1.25),
  },
  clearButton: {
    justifyContent: 'center',
    flexDirection: 'row',
    height: '100%',
    alignItems: 'flex-start',
    marginLeft: grid(2),
    paddingVertical: grid(1.25),
  },
  indentedLabel: {
    marginLeft: grid(7),
  },
  content: {
    flexGrow: 1,
    flexShrink: 1,
    paddingVertical: grid(1.5),
  },
  compactRow: {
    minHeight: grid(4),
    paddingTop: 0,
  },
  compactRowBottom: {
    minHeight: grid(4),
    paddingBottom: 0,
  },
  compactContentTop: {
    paddingTop: 0,
  },
  compactContentBottom: {
    paddingBottom: 0,
  },
  error: {
    paddingLeft: grid(3),
    paddingTop: grid(1),
    paddingBottom: grid(2),
    alignSelf: 'flex-start',
  },
});
